import { Link, useRouteLoaderData } from 'react-router-dom';
import type { RootData } from '../../App';
import { formatLargeInt, formatWeekLabel } from '../../lib/format';
import { usePageMeta } from '../../lib/use-page-meta';
import styles from './PaperPage.module.css';

export function PaperPage() {
  const root = useRouteLoaderData('root') as RootData | undefined;
  const meta = root?.meta;

  usePageMeta({
    title: 'Working paper · ViBE',
    description:
      'The full ViBE working paper: design rationale, prior-work positioning, the structured judge rubric, benchmark comparisons, and limitations.',
  });

  return (
    <article className={styles.page}>
      <header className={`${styles.header} container-wide`}>
        <p className={styles.eyebrow}>Working paper · Issue {meta?.week ?? ''}</p>
        <h1 className={styles.headline}>
          ViBE: a one-week Twitter benchmark for frontier model families
        </h1>
        <p className={styles.byline}>
          {meta ? formatWeekLabel(meta.week, meta.week_slug) : ''}
          {meta ? ` · ${formatLargeInt(meta.total_scored_mentions)} scored mentions across ${meta.total_models} tracked families` : ''}
        </p>

        <p className={styles.standfirst}>
          The full benchmark write-up — design rationale, prior-work positioning, the structured judge rubric,
          sensitivity analyses, agreement statistics across alternative judges, and the limitations the website
          summary skips over. The website tells you how it feels; the paper tells you why we trust it.
        </p>

        <div className={styles.actionsRow}>
          <a
            href="/paper/latest/index.html"
            className={styles.primaryAction}
            target="_blank"
            rel="noreferrer noopener"
          >
            Read the full paper →
          </a>
          <Link to="/methodology" className={styles.secondaryAction}>
            Or read the short methodology
          </Link>
        </div>
      </header>

      <hr className={`rule-strong container-wide ${styles.rule}`} />

      <section className={`${styles.embed} container-wide`}>
        <header className={styles.embedHeader}>
          <p className={styles.embedEyebrow}>Inline preview</p>
          <p className={styles.embedNote}>
            Rendered from the same artifact the working paper is published from. Open in a new tab for the
            full reading experience.
          </p>
        </header>
        <div className={styles.frameWrap}>
          <iframe
            className={styles.frame}
            src="/paper/latest/index.html"
            title="ViBE working paper"
            loading="lazy"
          />
        </div>
      </section>

      <section className={`${styles.contrast} container-wide`}>
        <p className={styles.contrastEyebrow}>Why two surfaces</p>
        <p className={styles.contrastBody}>
          The website is the public reception benchmark, executed at glance speed. The paper is the methodological
          undercarriage. The two are written in different registers on purpose. If a sentence on the website sounds
          like it belongs in an appendix, it is probably wrong for the website. If a sentence in the paper sounds
          like it belongs on a landing page, it is probably too simple for the paper.
        </p>
      </section>
    </article>
  );
}
